<template>
  <div class="ss" :class="{ 'is-open': open }">
    <input
      ref="input"
      class="ss__input"
      type="text"
      role="combobox"
      autocomplete="off"
      spellcheck="false"
      aria-autocomplete="list"
      :aria-label="label"
      :aria-expanded="open ? 'true' : 'false'"
      :aria-controls="listId"
      :aria-activedescendant="open && filtered[active] ? optionId(active) : null"
      :placeholder="open && selected ? selected.text : placeholder"
      :value="open ? query : (selected ? selected.text : '')"
      @focus="openList"
      @click="openList"
      @input="onInput"
      @keydown="onKeydown"
      @blur="onBlur"
    />
    <span class="ss__chevron" aria-hidden="true">▼</span>
    <!-- mousedown.prevent keeps focus in the input while picking or scrolling. -->
    <ul v-show="open" :id="listId" ref="list" class="ss__list" role="listbox" :aria-label="label" @mousedown.prevent>
      <li
        v-for="(o, i) in filtered"
        :id="optionId(i)"
        :key="String(o.value)"
        role="option"
        :aria-selected="o.value === value ? 'true' : 'false'"
        class="ss__option"
        :class="{ 'is-active': i === active, 'is-any': o.value === '' }"
        @click="choose(o)"
        @mousemove="active = i"
      >{{ o.text }}</li>
      <li v-if="!filtered.length" class="ss__empty" role="presentation">No matches</li>
    </ul>
  </div>
</template>

<script>
// Case- and accent-insensitive, so "eireann" finds "Éireann".
const fold = s => String(s).normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()

// A dropdown you can type into to filter. v-model holds the chosen value,
// '' for none. Options are strings or { value, text }.
export default {
  props: {
    value: { type: [String, Number], default: '' },
    options: { type: Array, default: () => [] },
    placeholder: String,
    label: String,
    anyLabel: { type: String, default: 'Any' },
  },
  data() {
    return {
      open: false,
      query: '',
      active: -1,
    }
  },
  computed: {
    listId() {
      return `ss-list-${this._uid}`
    },
    items() {
      return this.options.map(o => (typeof o === 'object' && o !== null)
        ? { value: o.value, text: String(o.text).trim() }
        : { value: o, text: String(o).trim() })
    },
    selected() {
      return this.value === '' || this.value === null ? null : this.items.find(o => o.value === this.value) || null
    },
    filtered() {
      const q = fold(this.query.trim())
      if (!q) return [{ value: '', text: this.anyLabel }, ...this.items]
      return this.items.filter(o => fold(o.text).includes(q))
    },
  },
  methods: {
    optionId(i) {
      return `${this.listId}-${i}`
    },
    openList() {
      if (this.open) return
      // Start with an empty box so typing searches from scratch; the current
      // choice shows as the placeholder meanwhile.
      this.query = ''
      this.$refs.input.value = ''
      this.open = true
      this.active = Math.max(0, this.filtered.findIndex(o => o.value === this.value))
      this.$nextTick(this.scrollToActive)
    },
    close() {
      this.open = false
      this.query = ''
    },
    choose(o) {
      this.$emit('input', o.value)
      this.close()
    },
    onInput(e) {
      if (!this.open) this.openList()
      this.query = e.target.value
      this.active = this.filtered.length ? 0 : -1
      this.$nextTick(this.scrollToActive)
    },
    onBlur() {
      if (!this.open) return
      // Leaving with an exact name typed counts as choosing it.
      const q = fold(this.query.trim())
      const match = q && this.items.find(o => fold(o.text) === q)
      if (match) this.$emit('input', match.value)
      this.close()
    },
    onKeydown(e) {
      const last = this.filtered.length - 1
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          if (!this.open) return this.openList()
          this.active = Math.min(this.active + 1, last)
          break
        case 'ArrowUp':
          e.preventDefault()
          if (!this.open) return this.openList()
          this.active = Math.max(this.active - 1, 0)
          break
        case 'Enter':
          if (!this.open) return
          e.preventDefault()
          if (this.filtered[this.active]) this.choose(this.filtered[this.active])
          else this.close()
          return
        case 'Escape':
          if (!this.open) return
          e.preventDefault()
          this.close()
          return
        default:
          // Typing into a closed box (just after picking): open first, so
          // the character lands in the emptied search rather than after
          // the chosen name.
          if (!this.open && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) this.openList()
          return
      }
      this.$nextTick(this.scrollToActive)
    },
    scrollToActive() {
      const el = this.$refs.list && this.$refs.list.children[this.active]
      if (el) el.scrollIntoView({ block: 'nearest' })
    },
  },
}
</script>

<style scoped>
.ss {
  position: relative;
  min-width: 0;
}
.ss__input {
  display: block;
  width: 100%;
  min-width: 0;
  padding: 16px 36px 16px 18px;
  border: 0;
  border-radius: 10px;
  background: #FFFFFF;
  font: 15px/1.2 'Montserrat', sans-serif;
  color: #3F3A32;
  text-overflow: ellipsis;
  cursor: pointer;
}
.is-open .ss__input {
  cursor: text;
}
.ss__input::placeholder {
  color: #9A9287;
  opacity: 1;
}
.ss__input:focus-visible {
  outline: 3px solid #C99BC7;
  outline-offset: 2px;
}
.ss__chevron {
  position: absolute;
  top: 50%;
  right: 18px;
  transform: translateY(-50%);
  font-size: 9px;
  color: #675B46;
  pointer-events: none;
  transition: transform .15s;
}
.is-open .ss__chevron {
  transform: translateY(-50%) rotate(180deg);
}
.ss__list {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  z-index: 20;
  max-height: 280px;
  margin: 0;
  padding: 6px;
  overflow-y: auto;
  overscroll-behavior: contain;
  list-style: none;
  border-radius: 10px;
  background: #FFFFFF;
  box-shadow: 0 12px 32px rgba(42, 0, 40, .22);
  text-align: left;
}
.ss__option,
.ss__empty {
  padding: 10px 12px;
  border-radius: 8px;
  font: 15px/1.3 'Montserrat', sans-serif;
  color: #3F3A32;
}
.ss__option {
  cursor: pointer;
}
.ss__option.is-active {
  background: #F6EFF5;
}
.ss__option[aria-selected='true'] {
  font-weight: 600;
  color: #560354;
}
.ss__option.is-any,
.ss__empty {
  color: #8A8074;
}
.ss__empty {
  font-size: 14px;
}
/* 16px stops iOS Safari zooming the page when the field is focused. */
@media (max-width: 639px) {
  .ss__input {
    font-size: 16px;
  }
  .ss__option {
    min-height: 44px;
    display: flex;
    align-items: center;
  }
}
</style>
